'use client';

import { redirectToLogin, redirectToLogout } from '@/lib/utils/auth-urls';
import { usePetOwnerOnboarding } from '@/lib/utils/petOwnerOnboarding';
import AppLayout from '@ui/layout/Layout';
import '../transitions.css';

export default function Layout({ children }: React.PropsWithChildren) {
  // Use the onboarding helper to get all necessary state and navigation
  const {
    isAuthenticated,
    isLoading,
    needsOnboarding,
    currentUserProfile,
    navigation,
  } = usePetOwnerOnboarding();

  // Show loading state while determining user state
  if (isLoading) {
    return (
      <AppLayout
        navigation={[]}
        userInfo={{
          name: 'Loading...',
          role: 'Loading',
          photoUrl: undefined,
        }}
        logoSrc="https://petbooststorage.blob.core.windows.net/petboost-image-library/1Petboost-BETA.svg"
        logoCollapsedSrc="https://petbooststorage.blob.core.windows.net/petboost-image-library/Paw-logo.svg"
        primaryColor="#ff5349" // Petboost red
        logoutHandler={() => {}}
        isAuthenticated={false}
        transitionClass="h-full [&>*]:motion-safe:duration-[2000ms] [&>*]:motion-safe:ease-[cubic-bezier(0.4,0,0.2,1)]"
      >
        <div className="flex items-center justify-center h-full">
          <div>Loading...</div>
        </div>
      </AppLayout>
    );
  }

  // Handlers for login/logout - Updated to use centralized auth utilities
  const handleAuthAction = () => {
    if (isAuthenticated) {
      // Reset PostHog identification
      if (typeof window !== 'undefined' && (window as any).posthog) {
        (window as any).posthog.reset();
      }

      // Clear localStorage
      localStorage.clear();

      // Clear sessionStorage
      sessionStorage.clear();

      // Use centralized logout redirect with normalized domain
      redirectToLogout();
    } else {
      // For anonymous users, handle login with current URL preservation
      redirectToLogin({ preserveCurrentUrl: true });
    }
  };

  // Create custom user info object based on authentication status
  const userInfo = isAuthenticated
    ? {
        name: currentUserProfile?.userInfo?.name,
        role: currentUserProfile?.userInfo?.role,
        photoUrl: currentUserProfile?.userInfo?.photoUrl ?? undefined,
      }
    : {
        name: 'Logged Out',
        role: 'Pet Owner',
        photoUrl: undefined,
      };

  return (
    <AppLayout
      navigation={navigation}
      userInfo={userInfo}
      logoSrc="https://petbooststorage.blob.core.windows.net/petboost-image-library/1Petboost-BETA.svg"
      logoCollapsedSrc="https://petbooststorage.blob.core.windows.net/petboost-image-library/Paw-logo.svg"
      primaryColor="#ff5349" // Petboost red
      logoutHandler={handleAuthAction}
      isAuthenticated={isAuthenticated}
      transitionClass="h-full [&>*]:motion-safe:duration-[2000ms] [&>*]:motion-safe:ease-[cubic-bezier(0.4,0,0.2,1)]"
    >
      {children}
    </AppLayout>
  );
}
